import { createFileRoute, getRouteApi, Navigate, useNavigate } from "@tanstack/react-router";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { EditCardPage } from "@/components/edit-card/EditCardPage";
import type { Card } from "@stackje/common/config";
import { cardParserRegistry } from "@/components/shared/form-parsers/cardParsers";
import { ValidationError, formatValidationErrors } from "@/components/shared/form-parsers/validation";
import { SitesRepository } from "@/repositories/SitesRepository";
import { useInvalidateSiteCache } from "@/hooks/useSiteQuery";

export const Route = createFileRoute("/_protected/_site/edit-card/$cardIndex")({
  component: RouteComponent,
});

function RouteComponent() {
  const { cardIndex } = Route.useParams();
  const cardIndexNumber = parseInt(cardIndex);

  const routeApi = getRouteApi("/_protected/_site");
  const data = routeApi.useLoaderData();
  const card = data.config?.pages[0].cards[cardIndexNumber];
  const invalidateSiteCache = useInvalidateSiteCache();
  const navigate = useNavigate();
  const { toast } = useToast();

  const onSubmit = async (formData: FormData) => {
    const cardType = formData.get("type");
    if (!cardType || typeof cardType !== "string") {
      return;
    }

    const parser = cardParserRegistry[cardType];
    if (!parser) {
      throw new Error("Invalid or unsupported card type");
    }

    let cardData: Card;
    try {
      cardData = parser(formData);
    } catch (e) {
      if (e instanceof ValidationError) {
        const errorMessages = formatValidationErrors(e.errors);
        throw new Error("Validation failed:\n" + errorMessages.join("\n"));
      }
      throw new Error("Unable to parse card data: " + e);
    }

    const page = data.config?.pages[0];
    if (!page) {
      throw new Error("Page not found");
    }

    page.cards[cardIndexNumber] = cardData;

    try {
      const { error } = await SitesRepository.update(data.id, data.userId, {
        config: {
          ...data.config,
        },
      });
      if (error) {
        throw error;
      }

      invalidateSiteCache(data.userId);
      toast({ description: "Card saved." });
      navigate({ to: "/" });
    } catch (e) {
      throw new Error("Unable to update card: " + e);
    }
  };

  const onDelete = async () => {
    const page = data.config?.pages[0];
    if (!page) {
      throw new Error("Page not found");
    }

    page.cards.splice(cardIndexNumber, 1);

    try {
      const { error } = await SitesRepository.update(data.id, data.userId, {
        config: {
          ...data.config,
        },
      });
      if (error) {
        throw error;
      }

      invalidateSiteCache(data.userId);
    } catch (e) {
      throw new Error("Unable to delete card: " + e);
    }
  };

  if (!card) {
    return <Navigate to="/" />;
  }

  return (
    <EditCardPage
      card={card}
      cardIndex={cardIndexNumber}
      onSubmit={onSubmit}
      onDelete={onDelete}
    />
  );
}
